import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FullStory } from '@fullstory/browser';
import { ElevateIcons } from '@hg-akademie-ban/ban-ui-assets/icons';
import { CheckIcon } from 'lucide-react';
import { useState } from 'react';
import {
  mapBrowserLanguageTagToTutorLanguage,
  TUTOR_LANGUAGES,
  type TutorLanguage,
  UNSUPPORTED_LANGUAGE_FALLBACK,
} from 'src/types/tutorLanguage';

import { useAuth } from '~ai-tutor/lib/contexts/auth';
import { syncTutorUiLanguage, useTranslation } from '~ai-tutor/lib/i18n';

import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdownMenu';

const LANGUAGE_SHORT_CODES: Record<TutorLanguage, string> = {
  de: 'DE',
  en: 'EN',
};

/** Native language names — shown the same regardless of the active UI language. */
const LANGUAGE_NATIVE_LABELS: Record<TutorLanguage, string> = {
  de: 'Deutsch',
  en: 'English',
};

function resolveCurrentTutorLanguage(i18nLanguage: string): TutorLanguage {
  return mapBrowserLanguageTagToTutorLanguage(i18nLanguage) ?? UNSUPPORTED_LANGUAGE_FALLBACK;
}

type LanguageSwitcherProps = {
  disabled?: boolean;
};

export function LanguageSwitcher({ disabled = false }: Readonly<LanguageSwitcherProps>) {
  const { t, i18n } = useTranslation();
  const { currentConversation } = useAuth();
  const [open, setOpen] = useState(false);

  const currentLanguage = resolveCurrentTutorLanguage(i18n.language);

  const handleSelect = async (newLanguage: TutorLanguage) => {
    if (newLanguage === currentLanguage) {
      setOpen(false);
      return;
    }

    FullStory('trackEvent', {
      name: 'lernassistent.settings.language.change',
      properties: {
        previousLanguage: currentLanguage,
        newLanguage,
        businessUnit: currentConversation?.businessUnit,
      },
    });

    await syncTutorUiLanguage(newLanguage);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="hover:bg-accent cursor-pointer transition-all"
          title={t('settings.language.label')}
          aria-label={t('settings.language.label')}
          disabled={disabled}
        >
          <FontAwesomeIcon className="size-4" icon={ElevateIcons.solid.faGlobe} />
          <span className="text-sm font-medium tracking-wide">{LANGUAGE_SHORT_CODES[currentLanguage]}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40 p-10">
        {TUTOR_LANGUAGES.map((language) => {
          const isSelected = language === currentLanguage;

          return (
            <DropdownMenuItem
              key={language}
              className="cursor-pointer justify-between gap-12"
              onSelect={() => {
                void handleSelect(language);
              }}
            >
              <span>{LANGUAGE_NATIVE_LABELS[language]}</span>
              <span className="flex size-5 shrink-0 items-center justify-center">
                {isSelected && <CheckIcon className="size-5" aria-hidden="true" />}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
